import { useQuery } from '@tanstack/react-query';
import { concat, get, isEmpty, uniq } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import { ISchedule, IScheduleItem } from 'src/interfaces/Schedule';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

import { fetchComponents, SCHEDULE_COMPONENT_INCLUDES } from '../components';
import { getPagesToFetch, setOrReplaceParam } from './helper';

const buildBaseParams = (filters: string[]) => concat(filters, ['count=true']);

const fetchScheduleOverviewPage = (params: string[]) =>
  requestCCApi({
    url: `/scheduleOverview${generateQuery(['dayparts'], params)}`,
  });

const mapScheduleItemsWithComponents = async (items: IScheduleItem[]) => {
  if (!isEmpty(items)) {
    const componentIDs = uniq(items.map((item) => item.component_id));
    const backupComponentIDs = uniq(
      items
        .flatMap((item) => (Array.isArray(item.backups) ? item.backups : []))
        .map((backup) => backup?.component_id),
    );
    const allComponentIDs = uniq([...componentIDs, ...backupComponentIDs]);
    const componentKeys = `id.list=${allComponentIDs.join('|')}`;
    const components = await queryClient.fetchQuery({
      queryKey: queryKeys.components.all(componentKeys),
      queryFn: () =>
        fetchComponents(
          [componentKeys, 'pageSize=0'],
          false,
          SCHEDULE_COMPONENT_INCLUDES,
        ),
    });
    const componentMap = new Map<number, any>(
      components.map((comp) => [comp.id, comp]),
    );

    return items.map((item) => ({
      ...item,
      component: componentMap.get(item.component_id),
      backups: Array.isArray(item.backups)
        ? item.backups.map((backup: any) => ({
            ...backup,
            component: componentMap.get(backup.component_id),
          }))
        : item.backups,
    }));
  }

  return items;
};

export const fetchSchedules = async (filters: string[]) => {
  const url = `/schedules${generateQuery([], filters)}`;

  const response = await requestCCApi({ url });

  return response.data ?? [];
};

export const useGetPaginatedSchedules = (
  filters: string[] = [],
  enabled: boolean = true,
  fetchAll: boolean = false,
) => {
  const queryParams = buildBaseParams(filters);

  return useQuery({
    queryKey: queryKeys.scheduleOverview.pagination([...queryParams]),
    enabled,
    queryFn: async () => {
      const firstResponse = await fetchScheduleOverviewPage(queryParams);
      const totalPages = get(firstResponse, 'totalPages', 0);
      const totalCount = get(firstResponse, 'totalCount', 0);
      const firstData = get(firstResponse, 'data');
      let aggregatedSchedules: ISchedule[] | undefined = Array.isArray(
        firstData,
      )
        ? (firstData as ISchedule[])
        : undefined;
      let aggregatedScheduleItems: IScheduleItem[] = get(
        firstResponse,
        'data.scheduleItems',
        [],
      );

      if (fetchAll && totalPages > 1) {
        const pagesToFetch = getPagesToFetch(totalPages, queryParams);

        const results = await Promise.all(
          pagesToFetch.map((p) =>
            fetchScheduleOverviewPage(
              concat(setOrReplaceParam(filters, 'page', p), [`count=true`]),
            ),
          ),
        );

        results.forEach((resp) => {
          const pageData = get(resp, 'data');
          if (Array.isArray(pageData)) {
            aggregatedSchedules = (aggregatedSchedules || []).concat(
              pageData as ISchedule[],
            );
          } else {
            const pageItems = get(resp, 'data.scheduleItems', []);
            aggregatedScheduleItems = aggregatedScheduleItems.concat(pageItems);
          }
        });
      }

      if (aggregatedSchedules && aggregatedSchedules.length > 0) {
        const allItems = aggregatedSchedules.flatMap(
          (s) => s.scheduleItems || [],
        );
        const mapped = await mapScheduleItemsWithComponents(allItems);

        let idx = 0;
        aggregatedSchedules = aggregatedSchedules.map((s) => {
          const count = (s.scheduleItems || []).length;
          const slice = mapped.slice(idx, idx + count);
          idx += count;

          return { ...s, scheduleItems: slice } as ISchedule;
        });

        return {
          schedulesData: aggregatedSchedules,
          totalSchedulesPages: fetchAll ? 1 : totalPages,
          totalCount,
        };
      }

      if (!isEmpty(aggregatedScheduleItems)) {
        const mappedItems = await mapScheduleItemsWithComponents(
          aggregatedScheduleItems,
        );
        firstResponse.data.scheduleItems = mappedItems;
      }

      return {
        schedulesData: firstResponse.data as ISchedule[],
        totalSchedulesPages: fetchAll ? 1 : totalPages,
        totalCount,
      };
    },
  });
};

export const useGetSchedules = (
  filters: string[] = [],
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.schedules.all(filters),
    queryFn: () => fetchSchedules(filters),
    enabled: enabled,
  });
};
